import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeftRight, Inbox, Send, Check, X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { PageHeader, EmptyState, shortDate } from "../components/common";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

export default function Transfers() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["transfers"],
    queryFn: async () => (await api.get("/transfers")).data,
  });

  const act = async (id, action) => {
    try {
      await api.post(`/transfers/${id}/${action}`);
      toast.success(action === "accept" ? "Transfer accepted — added to your wallet" : "Transfer declined");
      qc.invalidateQueries({ queryKey: ["transfers"] });
      qc.invalidateQueries({ queryKey: ["mine"] });
    } catch (err) { toast.error(apiError(err)); }
  };

  const incoming = data?.incoming || [];
  const outgoing = data?.outgoing || [];

  return (
    <AppLayout>
      <PageHeader eyebrow="Ownership" title="Transfers" subtitle="Send receipts to new owners, or accept ones sent to you." />

      {isLoading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <section>
            <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold"><Inbox className="h-5 w-5 text-brand" />Incoming</h2>
            {incoming.length === 0 ? (
              <EmptyState icon={Inbox} title="No incoming transfers" subtitle="Receipts sent to your email will appear here." />
            ) : (
              <div className="space-y-3">
                {incoming.map((t) => (
                  <div key={t.id} className="rounded-xl border border-border bg-card p-5" data-testid={`incoming-${t.id}`}>
                    <p className="font-heading font-bold">{t.item_summary}</p>
                    <p className="text-xs text-muted-foreground">{t.store_name} · from {t.from_email} · {shortDate(t.created_at)}</p>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" onClick={() => act(t.id, "accept")} className="bg-brand text-white hover:bg-brand-hover" data-testid={`accept-${t.id}`}><Check className="mr-1 h-4 w-4" />Accept</Button>
                      <Button size="sm" variant="outline" onClick={() => act(t.id, "decline")} data-testid={`decline-${t.id}`}><X className="mr-1 h-4 w-4" />Decline</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold"><Send className="h-5 w-5 text-brand" />Outgoing</h2>
            {outgoing.length === 0 ? (
              <EmptyState icon={ArrowLeftRight} title="No outgoing transfers" subtitle="Transfer a receipt from its detail page." />
            ) : (
              <div className="space-y-3">
                {outgoing.map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-5" data-testid={`outgoing-${t.id}`}>
                    <div>
                      <p className="font-heading font-bold">{t.item_summary}</p>
                      <p className="text-xs text-muted-foreground">to {t.to_email} · {shortDate(t.created_at)}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${t.status === "accepted" ? "bg-success/12 text-success" : t.status === "declined" ? "bg-destructive/12 text-destructive" : "bg-muted text-muted-foreground"}`}>{t.status}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </AppLayout>
  );
}
